import { useCallback, useRef, useEffect, useState, memo } from 'react';
import { CloudUpload, Pen, Trash, Download } from 'lucide-react';
import * as Sentry from '@sentry/react';

import { getDownloadURL, uploadFile } from '@/client/media';
import { getByPath } from '@/utils/widgets';
import { notify } from '@/components/toast';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { getFileIconStr } from '@/utils/fileTypes';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';
import { cn } from '@/shadcn/lib/utils';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import { type FileCompactData } from '@/components/ui/widget/views/fileCompact/fileCompact.view';
import { type WidgetData } from '@/components/ui/widget/widget.type';

type Props = {
  path: string;
  data: WidgetData;
  fileData: FileCompactData | undefined;
  onChange: (data: { path: string; value: unknown }) => void;
  setIsPatched: (isPatched: boolean) => void;
  disabled?: boolean;
  defaultIcon?: string;
};

export const UploadFile = memo(
  ({
    path,
    data,
    fileData,
    onChange,
    setIsPatched,
    disabled = false,
    defaultIcon = ''
  }: Props) => {
    const [isLoading, setIsloading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleNewFile = useCallback(
      async (evt: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = evt.target.files?.[0];
        if (!inputFile) return;

        await uploadFile(
          inputFile,

          // onSuccess
          file => {
            onChange({ path, value: file });
            setIsloading(false);
          },

          // onError
          () => {
            notify.error(`Document could not be uploaded`);
          },

          // onProgress
          () => {
            setIsloading(true);
          },

          // Disable encryption
          false
        );
      },
      [path, onChange]
    );

    const removeFile = () => onChange({ path, value: undefined });

    useEffect(() => {
      const currentData = getByPath<FileCompactData>(data, path);
      if (setIsPatched) {
        setIsPatched(currentData !== fileData);
      }
    }, [fileData]);

    const openBrowserFile = () => {
      if (!inputRef.current) {
        notify.error('Error: No file input found');
        Sentry.captureException(
          new Error('No file input found when opening browser file')
        );
        return;
      }
      inputRef.current.click();
    };

    const iconStr = getFileIconStr(
      fileData?.mimetype,
      fileData?.name,
      defaultIcon || 'lucide_FileDown'
    );

    if (disabled)
      return (
        <div
          className={cn(
            'relative flex size-full cursor-pointer items-center px-2.5',
            !fileData && 'cursor-not-allowed'
          )}
        >
          {fileData ? (
            <a
              className="text-foreground flex h-full grow cursor-pointer flex-row flex-nowrap items-center justify-start overflow-hidden"
              href={getDownloadURL(fileData)}
              target="_blank"
              rel="noreferrer"
            >
              <DynamicIcon icon={iconStr} size={24} />
              <TextEllipsis
                className="ml-[5px] grow overflow-hidden underline"
                text={fileData?.name}
                middleEllipsis
              />
            </a>
          ) : null}
        </div>
      );

    return (
      <LoadingIndicator spinning={isLoading} showSuccess={false}>
        <div className="relative flex size-full cursor-pointer items-center px-2.5">
          <input ref={inputRef} type="file" hidden onChange={handleNewFile} />
          {fileData ? (
            <div
              className="text-foreground flex size-full items-center justify-between gap-1"
              data-cy="upload-file-actions"
            >
              <TextEllipsis
                className="text-xs"
                text={fileData.name}
                onClick={openBrowserFile}
              />
              <TooltipProvider>
                <div className="flex shrink-0 items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-edit">
                      <Pen
                        className="text-muted-foreground hover:text-primary cursor-pointer"
                        onClick={openBrowserFile}
                        size={15}
                      />
                    </TooltipTrigger>
                    <TooltipContent>Edit file</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-download">
                      <a
                        href={getDownloadURL(fileData)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Download
                          className="text-muted-foreground hover:text-primary cursor-pointer"
                          size={15}
                        />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>Download file</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-delete">
                      <Trash
                        className="text-muted-foreground hover:text-destructive cursor-pointer"
                        size={15}
                        onClick={removeFile}
                      />
                    </TooltipTrigger>
                    <TooltipContent>Delete file</TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
          ) : (
            <div
              className="text-muted-foreground hover:text-primary flex size-full items-center gap-2"
              onClick={openBrowserFile}
              aria-label="Click to upload a file"
            >
              Upload a file
              <CloudUpload size={17} />
            </div>
          )}
        </div>
      </LoadingIndicator>
    );
  }
);
